//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  IContentLibrarySourcesOptions,
  MediaFilterParamKey,
} from '@apis/content-library/contentLibraryApiModel';
import {
  DEFAULT_DISTRIBUTED_TO_FILTER_SECTION,
  DEFAULT_DISTRIBUTION_FILTER_SECTION,
} from '@components/media-filters/DistributionFilter';
import { DEFAULT_MEDIA_TYPE_FILTER_SECTION } from '@components/media-filters/MediaTypeFilter';
import {
  FilterSection,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { ALL_MEDIA_SOURCES } from '@content-drawer/filters/MediaSourcesDropdown';
import { MediaFilterPanel } from '@content-drawer/media/MediaFilterPanel';
import { MediaPanel } from '@content-drawer/media/MediaPanel';
import {
  getDisabledFilters,
  getInitialFilters,
} from '@content-drawer/selectors/getContentDrawerSettings';
import { contentDrawerActions } from '@redux/content-drawer/contentDrawerActions';
import { IContentDrawerFilters } from '@redux/content-drawer/contentDrawerModel';
import { isDefined, isUndefined } from '@utils/filterPredicates';

export interface ContentDrawerProps {
  onClose: () => void;
}

const getDefaultMediaTypeSection = (mediaTypes?: readonly string[]) => {
  if (isDefined(mediaTypes) && mediaTypes.length > 0) {
    const section = DEFAULT_MEDIA_TYPE_FILTER_SECTION;
    const newSection: FilterSection<FilterValue> = {
      ...section,
      options: section.options.map((item) => ({
        ...item,
        isSelected: mediaTypes.includes(item.value),
      })),
    };
    return newSection;
  }
};

// The list isDistributed is coming from Angular and defines the default value for the distribution filter as stringified list of boolean values
const getDefaultIsDistributedSection = (isDistributed?: readonly string[]) => {
  if (isDistributed && isDistributed.length > 0) {
    const section = DEFAULT_DISTRIBUTION_FILTER_SECTION;
    const newSection: FilterSection<FilterValue> = {
      ...section,
      options: section.options.map((item) => ({
        ...item,
        isSelected: isDistributed.includes(`${item.value}`),
      })),
    };
    return newSection;
  }
};

const getDefaultDistributionSection = (distribution?: readonly string[]) => {
  if (isDefined(distribution) && distribution.length > 0) {
    const section = DEFAULT_DISTRIBUTED_TO_FILTER_SECTION;
    const newSection: FilterSection<FilterValue> = {
      ...section,
      options: distribution.map((item) => ({
        isSelected: true,
        label: item,
        value: item,
      })),
    };
    return newSection;
  }
};

const getDefaultSections = (
  initialFilters: IContentDrawerFilters | undefined,
) => {
  const filterSections = new Map<
    MediaFilterParamKey,
    FilterSection<FilterValue>
  >();

  const defaultMediaTypeSection = getDefaultMediaTypeSection(
    initialFilters?.mediaItemTypes,
  );
  const defaultIsDistributedSection = getDefaultIsDistributedSection(
    initialFilters?.isDistributed,
  );
  const defaultDistributionSection = getDefaultDistributionSection(
    initialFilters?.distribution,
  );

  if (isDefined(defaultMediaTypeSection)) {
    filterSections.set('mediaItemTypes', defaultMediaTypeSection);
  }

  if (isDefined(defaultIsDistributedSection)) {
    filterSections.set('isDistributed', defaultIsDistributedSection);
  }

  if (isDefined(defaultDistributionSection)) {
    filterSections.set('distribution', defaultDistributionSection);
  }

  return filterSections;
};

const getRequiredSections = (
  initialFilters: IContentDrawerFilters | undefined,
  disabledFilters: readonly string[] | undefined,
) => {
  if (isUndefined(initialFilters)) {
    return getDefaultSections(initialFilters);
  }

  const requiredFilters = (
    Object.keys(initialFilters) as (keyof IContentDrawerFilters)[]
  ).reduce<IContentDrawerFilters>((filters, filterKey) => {
    return disabledFilters?.includes(filterKey)
      ? { ...filters, [filterKey]: initialFilters[filterKey] }
      : filters;
  }, {});

  return getDefaultSections(requiredFilters);
};

export const ContentDrawer = ({ onClose }: ContentDrawerProps) => {
  const dispatch = useDispatch();

  const initialFilters = useSelector(getInitialFilters);
  const disabledFilters = useSelector(getDisabledFilters);

  const [sources, setSources] =
    useState<IContentLibrarySourcesOptions>(ALL_MEDIA_SOURCES);
  const [isFilterPanelVisible, setIsFilterPanelVisible] = useState(true);
  const [sections, setSections] = useState(getDefaultSections(initialFilters));

  const onChange = (section: FilterSection<FilterValue>) => {
    setSections((map) => new Map(map.set(section.paramKey, section)));
  };

  const onReset = () => {
    setSections(getRequiredSections(initialFilters, disabledFilters));
  };

  useEffect(() => {
    return () => {
      dispatch(contentDrawerActions.unsetOverrideSettings());
    };
  }, []);

  return (
    <>
      {isFilterPanelVisible && (
        <MediaFilterPanel
          onChange={onChange}
          onReset={onReset}
          sections={sections}
          sources={sources}
        />
      )}
      <MediaPanel
        onChangeFilters={onChange}
        onChangeSources={setSources}
        onClose={onClose}
        onResetFilters={onReset}
        sections={sections}
        sources={sources}
        toggleFilters={setIsFilterPanelVisible}
      />
    </>
  );
};
